import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Modal, Collapse, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    Utils,
    fetchWithAuth,
    filtersObjectHasAnyValue,
    findOptionByValue,
    SCHEDULED_MESSAGE_STATUS,
    MESSAGE_CREATED_BY_OPTIONS,
    MESSAGE_AUDIENCE_OPTIONS,
    formatDateTime,
    getDefaultPageSize,
    getTotalPages,
    can,
    getBrowserTimeZone,
    exportListCsv,
} from '../../utils';
import { useActionDropdown } from '../../hooks/useActionDropdown';
import { useTableMaxHeight } from '../../hooks/useTableMaxHeight';
import { useScrollEndDetection } from '../../hooks/useScrollEndDetection';
import ListPaginationBar from '../common/ListPaginationBar';
import { MdKeyboardArrowDown, MdAddCircle, MdFileDownload } from 'react-icons/md';
import { FiRefreshCw } from 'react-icons/fi';
import ScheduledMessagesFiltersBar from './ScheduledMessagesFiltersBar';

const EMPTY_SCHEDULED_FILTERS = { notificationId: '', status: '', createdBy: '', audience_type: '', dateFrom: '', dateTo: '', datePreset: '' };

const ScheduledMessages = () => {
    const { appId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_SCHEDULED_FILTERS }));
    const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_SCHEDULED_FILTERS }));
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = getDefaultPageSize();
    const [messagesTotal, setMessagesTotal] = useState(0);
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        const hasContent =
            filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
        if (!hasContent) {
            setFilterAccordionOpen(false);
            return;
        }
        setFilterAccordionOpen(true);
    }, [draftFilters, appliedFilters]);

    const getScheduledMessageActionOptionCount = (item) => {
      const pending = Number(item?.status) === findOptionByValue(SCHEDULED_MESSAGE_STATUS, 3)?.value;
      return (
        2 +
        (can("push.manage") ? 1 : 0) +
        (pending && can("push.manage") ? 2 : 0)
      );
    };
    const { openActionItem, dropdownPosition, dropdownRef, closeDropdown, handleOpenDropdown } = useActionDropdown({
      tableScrollRef,
      optionCount: getScheduledMessageActionOptionCount,
      menuWidth: 200,
    });
    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, messages?.length);

    const fetchScheduledMessages = async () => {
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'get/scheduled-notifications', {
                method: 'POST',
                body: JSON.stringify({
                    app_id: appId,
                    notification_id: (appliedFilters.notificationId || '').trim() || undefined,
                    status: appliedFilters.status !== '' && appliedFilters.status != null
                        ? appliedFilters.status
                        : undefined,
                    created_by: appliedFilters.createdBy !== '' && appliedFilters.createdBy != null
                        ? appliedFilters.createdBy
                        : undefined,
                    audience_type: appliedFilters.audience_type !== '' && appliedFilters.audience_type != null
                        ? appliedFilters.audience_type
                        : undefined,
                    ...(appliedFilters.dateFrom && appliedFilters.dateTo
                        ? {
                            date_from: appliedFilters.dateFrom,
                            date_to: appliedFilters.dateTo,
                            timezone: getBrowserTimeZone() || undefined,
                        }
                        : {}),
                    page: currentPage,
                    pageSize,
                }),
            });
            const result = await response.json();
            if (result.status) {
                setMessages(result.data || []);
                setMessagesTotal(result.meta?.total ?? (result.data?.length || 0));
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching scheduled messages:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!appId) return;
        setLoading(true);
        fetchScheduledMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, appliedFilters.notificationId, appliedFilters.status, appliedFilters.createdBy, appliedFilters.audience_type, appliedFilters.dateFrom, appliedFilters.dateTo, currentPage]);

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const cleared = { ...EMPTY_SCHEDULED_FILTERS };
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        setCurrentPage(1);
    };

    const totalRows = messagesTotal || 0;
    const totalPages = getTotalPages(totalRows, pageSize);
    const safePage = Math.min(Math.max(1, currentPage), totalPages);


    const handleNewMessage = () => {
        navigate(`/apps/${appId}/push-notification`);
    };

    const handleCancelNotification = async () => {
        if (!selectedMessage) return;

        try {
            const response = await fetchWithAuth(Utils.baseurl + 'cancel/scheduled-notification', {
                method: 'POST',
                body: JSON.stringify({ id: selectedMessage.id }),
            });
            const result = await response.json();
            if (result.status) {
                toast.success('Notification cancelled successfully');
                fetchScheduledMessages();
            } else {
                toast.error(result.message || 'Failed to cancel notification');
            }
        } catch (error) {
            console.error('Error cancelling notification:', error);
            toast.error('Error cancelling notification');
        }
        setShowCancelModal(false);
        setSelectedMessage(null);
    };

    const handleViewReport = (message) => {
        const notificationId = message?.notification_id;
        if (!notificationId) {
            toast.info('Report not available yet for this message.');
            return;
        }
        const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
        const qs = new URLSearchParams({
            activity_page: '1',
            activity_limit: String(pageSize),
            activity_filter: 'all',
            returnTo,
        });
        navigate(`/apps/${appId}/sent-messages/${notificationId}/report?${qs.toString()}`, {
            state: {
                listMessage: {
                    name: message.name,
                    title: message.title,
                    status: message.status,
                    sent_count: message.sent_count,
                    created_by: message.created_by,
                    sent_at: message.scheduled_at,
                    created_at: message.createdAt || message.created_at,
                },
            },
        });
    };

    const handleEdit = (message) => {
        navigate(`/apps/${appId}/push-notification`, {
            state: { editMessage: message },
        });
    };

    const handleDuplicate = async (message) => {
        try {
            const notificationData = message.notification_data ? JSON.parse(message.notification_data) : {};

            const duplicateData = {
                appId: message.app_id,
                name: `${message.name || message.title} (Copy)`,
                title: message.title,
                message: message.message,
                image: message.image,
                scheduledAt: new Date(Date.now() + 3600000).toISOString(),
                audience_type: message.audience_type,
                segmentIds: message.segment_ids ? JSON.parse(message.segment_ids) : null,
                subscriptionIds: message.subscription_ids ? JSON.parse(message.subscription_ids) : null,
                notificationData,
            };

            const response = await fetchWithAuth(Utils.baseurl + 'create/scheduled-notification', {
                method: 'POST',
                body: JSON.stringify(duplicateData),
            });
            const result = await response.json();
            if (result.status) {
                toast.success('Message duplicated successfully');
                fetchScheduledMessages();
            } else {
                toast.error(result.message || 'Failed to duplicate message');
            }
        } catch (error) {
            console.error('Error duplicating message:', error);
            toast.error('Error duplicating message');
        }
    };

    const handleCopyMessageId = (message) => {
        const messageId = message.id?.toString();
        if (messageId) {
            navigator.clipboard.writeText(messageId);
            toast.success('Message ID copied to clipboard!');
        } else {
            toast.warning('No message ID available');
        }
    };

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv('get/scheduled-notifications', {
                app_id: appId,
                notification_id: (appliedFilters.notificationId || '').trim() || undefined,
                status: appliedFilters.status !== '' && appliedFilters.status != null ? appliedFilters.status : undefined,
                created_by: appliedFilters.createdBy !== '' && appliedFilters.createdBy != null ? appliedFilters.createdBy : undefined,
                audience_type: appliedFilters.audience_type !== '' && appliedFilters.audience_type != null ? appliedFilters.audience_type : undefined,
                ...(appliedFilters.dateFrom && appliedFilters.dateTo
                    ? {
                        date_from: appliedFilters.dateFrom,
                        date_to: appliedFilters.dateTo,
                        timezone: getBrowserTimeZone() || undefined,
                    }
                    : {}),
            }, "scheduled-messages.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error('Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>Scheduled Messages</h1>
                    </div>
                    <div className="add_app" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => { setLoading(true); fetchScheduledMessages(); }}>
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
                            <MdFileDownload />
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                        </button>
                        {can("push.manage") ? (
                            <button type="button" className="add_app_btn btn_theme_primary" onClick={handleNewMessage}>
                                <MdAddCircle />
                                <span>New Message</span>
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className="list-page-filter-card">
                    <button
                        type="button"
                        className="list-page-filter-accordion-header"
                        onClick={() => setFilterAccordionOpen((o) => !o)}
                        aria-expanded={filterAccordionOpen}
                        aria-controls="scheduled-messages-filter-panel"
                        id="scheduled-messages-filter-accordion"
                    >
                        <span className="list-page-filter-accordion-title">Filter</span>
                        <MdKeyboardArrowDown
                            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? 'list-page-filter-accordion-chevron--open' : ''}`}
                            size={22}
                            aria-hidden
                        />
                    </button>
                    <Collapse in={filterAccordionOpen}>
                        <div
                            className="list-page-filter-accordion-body"
                            id="scheduled-messages-filter-panel"
                            role="region"
                            aria-labelledby="scheduled-messages-filter-accordion"
                        >
                            <ScheduledMessagesFiltersBar
                                draftFilters={draftFilters}
                                setDraftFilters={setDraftFilters}
                                onSearch={handleApplyFilters}
                                onReset={handleResetFilters}
                            />
                        </div>
                    </Collapse>
                </div>

                <div className="dashboard_app_table dashboard_app_table--list-records">
                    <div className="dashboard_table">
                        <div
                            ref={tableScrollRef}
                            className="table_scroll_wrapper"
                        >
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                }}
                            >
                                <table className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th>Notification ID</th>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th>Send At</th>
                                            <th>Audience</th>
                                            <th>Segments</th>
                                            <th>Source</th>
                                            <th className="col-action" style={{ textAlign: 'right' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : messages.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    No scheduled messages found
                                                </td>
                                            </tr>
                                        ) : (
                                            messages.map((message, index) => {
                                                const statusOpt = findOptionByValue(SCHEDULED_MESSAGE_STATUS, message.status);
                                                const createdOpt = findOptionByValue(
                                                    MESSAGE_CREATED_BY_OPTIONS,
                                                    message.created_by
                                                );
                                                const audienceOpt = findOptionByValue(
                                                    MESSAGE_AUDIENCE_OPTIONS,
                                                    message.audience_type
                                                );
                                                return (
                                                    <tr key={index}>
                                                        <td className="truncate">{message.notification_id || '-'}</td>
                                                        <td className="message-name">{message.name || message.title}</td>
                                                        <td>
                                                            {statusOpt ? (
                                                                <span className={statusOpt.badgeClass}>{statusOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>{formatDateTime(message.scheduled_at)}</td>
                                                        <td>
                                                            {audienceOpt ? (
                                                                <span className={audienceOpt.badgeClass}>{audienceOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {Array.isArray(message.segment_names) && message.segment_names.length
                                                                ? message.segment_names.join(', ')
                                                                : '—'}
                                                        </td>
                                                        <td>
                                                            {createdOpt ? (
                                                                <span className={createdOpt.badgeClass}>{createdOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td className="col-action" style={{ textAlign: 'right' }}>
                                                            <button
                                                                type="button"
                                                                className="dropdown-trigger-btn btn btn-link p-0"
                                                                aria-label="Actions"
                                                                onClick={(e) => handleOpenDropdown(e, message.id, message)}
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                                    <circle cx="8" cy="3" r="1.5" />
                                                                    <circle cx="8" cy="8" r="1.5" />
                                                                    <circle cx="8" cy="13" r="1.5" />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={safePage}
                                setPage={setCurrentPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Scheduled messages pagination"
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {openActionItem &&
                dropdownPosition &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        style={{
                            position: 'fixed',
                            left: dropdownPosition.left,
                            top: dropdownPosition.top,
                            zIndex: 10000,
                            minWidth: '220px',
                        }}
                    >
                        <div className="dropdown-menu-subscription">
                            <button
                                type="button"
                                onClick={() => {
                                    closeDropdown();
                                    handleViewReport(openActionItem);
                                }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ marginRight: '8px', flexShrink: 0 }}
                                >
                                    <path d="M8 3.5a.5.5 0 00-1 0V9a.5.5 0 00.252.434l3.5 2a.5.5 0 00.496-.868L8 8.71V3.5z" />
                                    <path d="M8 16A8 8 0 108 0a8 8 0 000 16zm7-8A7 7 0 111 8a7 7 0 0114 0z" />
                                </svg>
                                <span style={{ fontSize: '0.875rem' }}>View Report</span>
                            </button>
                            {Number(openActionItem.status) === findOptionByValue(SCHEDULED_MESSAGE_STATUS, 3)?.value && can("push.manage") && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        closeDropdown();
                                        handleEdit(openActionItem);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{ marginRight: '8px', flexShrink: 0 }}
                                    >
                                        <path d="M12.146.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.168.11l-5 2a.5.5 0 01-.65-.65l2-5a.5.5 0 01.11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 01.5.5v.5h.5a.5.5 0 01.5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 015 12.5V12h-.5a.5.5 0 01-.5-.5V11h-.5a.5.5 0 01-.468-.325z" />
                                    </svg>
                                    <span style={{ fontSize: '0.875rem' }}>Edit</span>
                                </button>
                            )}
                            {can("push.manage") ? (
                            <button
                                type="button"
                                onClick={() => {
                                    closeDropdown();
                                    handleDuplicate(openActionItem);
                                }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ marginRight: '8px', flexShrink: 0 }}
                                >
                                    <path d="M4 2a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V2zm2-1a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1V2a1 1 0 00-1-1H6z" />
                                    <path d="M2 6a2 2 0 012-2v10h10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                                <span style={{ fontSize: '0.875rem' }}>Duplicate</span>
                            </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => {
                                    closeDropdown();
                                    handleCopyMessageId(openActionItem);
                                }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ marginRight: '8px', flexShrink: 0 }}
                                >
                                    <path d="M4 2a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V2zm2-1a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1V2a1 1 0 00-1-1H6z" />
                                    <path d="M2 6a2 2 0 012-2v10h10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                                <span style={{ fontSize: '0.875rem' }}>Copy Message ID</span>
                            </button>
                            {Number(openActionItem.status) === findOptionByValue(SCHEDULED_MESSAGE_STATUS, 3)?.value && can("push.manage") && (
                                <button
                                    type="button"
                                    className="delete-class"
                                    onClick={() => {
                                        closeDropdown();
                                        setSelectedMessage(openActionItem);
                                        setShowCancelModal(true);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', color: 'red' }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{ marginRight: '8px', flexShrink: 0 }}
                                    >
                                        <path d="M8 15A7 7 0 118 1a7 7 0 010 14zm0-1A6 6 0 108 2a6 6 0 000 12z" />
                                        <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z" />
                                    </svg>
                                    <span style={{ fontSize: '0.875rem' }}>Cancel</span>
                                </button>
                            )}
                        </div>
                    </div>,
                    document.body
                )}

            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Cancel Scheduled Notification</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to cancel this scheduled notification?
                    <br />
                    <br />
                    <strong>{selectedMessage?.title}</strong>
                    <br />
                    Scheduled for: {selectedMessage && formatDateTime(selectedMessage.scheduled_at)}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                        Close
                    </Button>
                    {can("push.manage") ? (
                        <Button variant="danger" onClick={handleCancelNotification}>
                            Cancel Notification
                        </Button>
                    ) : null}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ScheduledMessages;
